<template>
	<div v-if="!arrayProperty" class="error">
		Property <code>{{ prop }}</code> must be array
	</div>
	<div v-else-if="visible && anythingToDisplay" class="fd-array" ref="array" :class="clazz" :style="style">
		<div v-if="0 === drop" class="fd-array-placeholder-item" :style="placeholderStyle"></div>
		<template v-for="(part, index) in partsArray" :key="part.key">
			<div class="fd-array-item" :style="itemStyle(index)" :data-index="index">
				<div class="fd-array-item-content">
					<component :is="componentOfPart(part)" :part="partRef(part)" :data="value" :readonly="ro" :viewmode="vm"
					   :array-index="index"
						@array-delete="deleteItem(index)"
						@array-before="insertItem(index, true)"
						@array-after="insertItem(index, false)"
					></component>
				</div>
				<sym-icon v-if="canRemove" :name="arr.removeIcon" class="fd-array-remove-item" @click="deleteItem(index)"></sym-icon>
				<sym-icon v-if="canSort" name="menu-18" class="fd-array-handle" @dragstart="false" v-on:[startEventName]="dragStart(index, $event)"></sym-icon>
			</div>
			<div v-if="dropAfter(index)" class="fd-array-placeholder-item" :style="placeholderStyle"></div>
		</template>

		<div v-if="partsArray.length === 0 && arr.placeholder" class="fd-array-placeholder"><component :is="componentOfPart(arr.placeholder)" :part="partRef(arr.placeholder)" :data="value" :readonly="ro" :viewmode="vm"></component></div>

		<div v-if="canAddMore" class="fd-array-add-item">
			<template v-for="btn in addButtons">
				<template v-if="visibleButton(btn)">
					<icon-button v-if="btn.label==null" :name="btn.icon" @click.prevent.stop="addItem(btn)" :class="btn.clazz"></icon-button>
					<button v-else v-preview="btn.webPreview" type="button" @click.prevent.stop="addItem(btn)"
							class="fd-array-add-button fbutton input-height" :class="btn.clazz">
						<sym-icon v-if="btn.icon" :name="btn.icon"></sym-icon>
						<span>{{ addLabel(btn) }}</span></button>
				</template>
			</template>
			<component v-if="hasExtraEdit" :is="componentOfPart(arr.edit)" :part="partRef(arr.edit)" :data="localData"
				:readonly="ro" :viewmode="vm"></component>
		</div>
		<div v-else-if="hasExtraEdit" class="fd-array-add-item">
			<component :is="componentOfPart(arr.edit)" :part="partRef(arr.edit)" :data="localData"
					   :readonly="ro" :viewmode="vm"></component>
		</div>
	</div>
</template>

<script setup lang="ts">
import useContainer, {type ContainerProps, containerProps} from "./Container";
import {getter} from "../Part";
import type {Array, ArrayAddButton} from "@t/fd/layout/Array";
import {computed, onMounted, onUnmounted, ref, watch} from "vue";
import SymIcon from "../helpers/SymIcon.vue";
import {injectEnv} from "../Environment";
import {getJson} from "../Backend";
import {nextTick} from 'vue'
import {injectFormData} from "../PartInjectKeys";
import IconButton from "@fd/helpers/IconButton.vue";
import {FormPart} from "@t/fd/FormPart.ts";
import {useAddButton} from "@fd/containers/AddButton.ts";

const uprops = defineProps({...containerProps});
const props = uprops as ContainerProps;
const env = injectEnv();

const arr = computed<Array>(() => props.part as Array);

const {visible, visibilityEval, clazz, style, componentOfPart, partRef, localData, ro, vm, prop, formData} = useContainer(props);

interface ElementSizing {
    top: number;
    height: number;
	/* Important CSS styles */
	marginTop: string;
	marginBottom: string;
}

const drag = ref(-1); /* index of dragged intem (-1 = nothing is dragged) */
const drop = ref(-1); /* index of item which will receive dropped item */
const dragLeft = ref(0);
const dragTop = ref(0);
const dragWidth = ref(0);
const dragHeight = ref(0);
const dragMarginTop = ref("0");
const dragMarginBottom = ref("0");

const startX = ref(0);
const startY = ref(0);
const startLeft = ref(0);
const startTop = ref(0);
const mouseX = ref(0);
const mouseY = ref(0);
const sizing = ref<ElementSizing[]>([]);
const array = ref<HTMLDivElement | null>(null);

const hasExtraEdit = computed<boolean>(() => !!arr.value.edit);
const startEventName = computed(() => {
    return env.touch ? "touchstart" : "mousedown";
});

const arrayProperty = computed<boolean>(() => {
    return Array.isArray(value.value);
});

const value = computed(() => {
    return getter(localData.value as FormData, prop.value, formData);
});

const itemPart = computed(() => {
    return partRef(arr.value.item);
});

const KEY_DATA_PROP = "_key";
let autoIdentityCounter = 1;

const partsArray = ref<FormPart[]>(initialItemParts());

function generatePartForIndex(index: number):FormPart {
	const part:FormPart = {...itemPart.value};
	if (arr.value.objectMode) {
		part.data = index;
	} else {
		part.property = index;
	}
	part.key = autoIdentityCounter++;
	return part;
}


/** Generate initial parts for available items */
function initialItemParts():FormPart[] {
	// console.log("initialItemParts: value="+JSON.stringify(value.value)+
	// 	"\n\npart="+JSON.stringify(arr.value));
	// console.dir(localData.value);
	const parts = [];
	if (value.value) {
		for (let index = 0; index < value.value.length; index++) {
			parts.push(generatePartForIndex(index));
		}
	}
	return parts;
}

const placeholderStyle = computed(() => {
    return {
        width: dragWidth.value + "px",
        height: dragHeight.value + "px",
		lineHeight: dragHeight.value + "px",
		marginTop: dragMarginTop.value,
		marginBottom: dragMarginBottom.value
    };
});

const canAddMore = computed(() => {
    let maxItems = arr.value.maxItems;
    if (typeof maxItems !== "number") maxItems = 0;
    return !ro.value && (maxItems === 0 || maxItems > value.value.length) && Array.isArray(arr.value.add) && arr.value.add.length > 0;
});
const addButtons = computed(() => {
    return arr.value.add;
});
const canSort = computed(() => {
    return !ro.value && arr.value.sortable;
});
const canRemove = computed(() => {
	let minItems = arr.value.minItems;
	if (typeof minItems !== "number") minItems = 0;
    return !ro.value && arr.value.removeIcon && value.value.length > minItems;
});
const anythingToDisplay = computed<boolean>(() => {
    return canAddMore.value || partsArray.value.length > 0;
});

function itemStyle(index: number) {
    const s: any = {};
    if (index === drag.value) {
        s.backgroundColor = "var(--bg)";
        s.boxShadow = "var(--panel-shadow)";
        s.position = "absolute";
        s.left = dragLeft.value + "px";
        s.top = dragTop.value + "px";
        s.width = dragWidth.value + "px";
        s.height = dragHeight.value + "px";
        s.zIndex = 10000;
    }
    return s;
}

function isTouch(e: TouchEvent | MouseEvent): e is TouchEvent {
    return env.touch;
}

function findArrayElements(): HTMLDivElement[] {
    const elms: HTMLDivElement[] = [];
    if (array.value) {
        let found = array.value.querySelectorAll(":scope > div.fd-array-item[data-index]");
        for (let el of found) {
            elms.push(el as HTMLDivElement);
        }
    }
    return elms;
}

function dragStart(index: number, e: TouchEvent | MouseEvent) {
    // prepare given element for dragging
	//console.log("Start drag of index " + index);
    const elms = findArrayElements();
    drag.value = index;
    drop.value = index;
    let i = 0;
    sizing.value.length = 0;
    for (let elm of elms) {
		const css = getComputedStyle(elm);
		//console.log("elm.offsetHeight = "+elm.offsetHeight+" css.height="+css.height+" css.marginTop="+css.marginTop+" css.marginBottom="+css.marginBottom);
        const size: ElementSizing = {
            top: elm.offsetTop,
            height: elm.offsetHeight,
			marginTop: css.marginTop,
			marginBottom: css.marginBottom,
        };
        sizing.value.push(size);
        if (i === index) {
            dragLeft.value = elm.offsetLeft;
            dragTop.value = elm.offsetTop;
            dragWidth.value = elm.offsetWidth;
            dragHeight.value = elm.offsetHeight;
            dragMarginTop.value = css.marginTop;
            dragMarginBottom.value = css.marginBottom;
        }
        i++;
    }
    // Save position
    startLeft.value = dragLeft.value;
    startTop.value = dragTop.value;
    startX.value = isTouch(e) ? e.touches[0].pageX : e.pageX;
    startY.value = isTouch(e) ? e.touches[0].pageY : e.pageY;
    e.preventDefault();
}

/** All parts must point to corrent data */
function correctPartIndexes() {
	let idx = 0;
	for (let p of partsArray.value) {
		if (arr.value.objectMode) {
			p.data = idx++;
		} else {
			p.property = idx++;
		}
	}
}

function dragStop() {
    if (drag.value !== drop.value) {
        const item = value.value[drag.value];
		const itemPart = partsArray.value[drag.value];

		// remove item from previous position
        value.value.splice(drag.value, 1);
		partsArray.value.splice(drag.value, 1);
		// insert item to the new position
        value.value.splice(drop.value, 0, item);
		partsArray.value.splice(drop.value, 0, itemPart);
		correctPartIndexes();
    }
    drag.value = -1;
    drop.value = -1;
}

function dragHandler() {
    if (drag.value >= 0) {
        const diffX = mouseX.value - startX.value;
        const diffY = mouseY.value - startY.value;
        dragTop.value = startTop.value + diffY;

        let minDist = 300000;
        let minIdx = -1;
        let idx = 0;
        for (let size of sizing.value) {
            const dist = Math.abs(size.top - dragTop.value);
            if (dist < minDist) {
                minDist = dist;
                minIdx = idx;
            }
            idx++;
        }
        drop.value = minIdx;
    }
}

function dropAfter(index: number) {
    return index + (drag.value >= drop.value ? 1 : 0) === drop.value;
}

const { completeEndpointUrl, addLabel, buildNewItem } = useAddButton(formData, value);

function visibleButton(btn: ArrayAddButton): boolean {
    return visibilityEval(btn, "visible");
}

function addItem(btn: ArrayAddButton | null, index?: number) {
	buildNewItem(btn, index).then((newItem) => {
		if (newItem != null) insertToData(newItem, index);
	});
}

function insertToData(item: any, index?: number) {
    if (typeof index === "number") {
        value.value.splice(index, 0, item);
		partsArray.value.splice(index, 0, generatePartForIndex(index));
		correctPartIndexes();
        focusItem(index);
    } else {
        value.value.push(item);
		partsArray.value.push(generatePartForIndex(partsArray.value.length));
        focusItem(value.value.length - 1);
    }
}

function deleteItem(index: number, focus = true):boolean {
	if (index < 0) index = value.value.length - 1;
	if (index < 0) return false;	// out of bounds
	if (typeof arr.value.minItems === "number") {
		if (value.value.length <= arr.value.minItems) return false;	// cannot delete more
	}
    value.value.splice(index, 1);
	partsArray.value.splice(index, 1);
	correctPartIndexes();
    if (focus) focusItem(index - 1);
	return true;
}

function insertItem(index: number, before: boolean) {
    if (!canAddMore.value || ro.value) return;
    const idx = before ? index : index + 1;
    let button: ArrayAddButton | null = null;
    if (Array.isArray(arr.value.add) && arr.value.add.length > 0) button = arr.value.add[0];
    addItem(button, idx);
}

function focusItem(index: number) {
    nextTick(() => {
        if (array.value && value.value.length > 0) {
            if (index < 0) index = 0;
            else if (index >= value.value.length) index = value.value.length - 1;
            let found = array.value.querySelector("div.fd-array-item[data-index=\"" + index + "\"]");
            if (found) {
                const focusElement = found.querySelector(".fd-array-focus") as HTMLElement;
                if (focusElement) {
                    focusElement.focus();
                }
            }
        }
    });
}

/**
 * Remove all items from the array until minimum count is reached
 * @return count of deleted items
 */
function discardItems():number {
	let count = 0;
	while (deleteItem(-1, false)) {
		count++;
	}
	return count;
}

let onTouchMove: { (event: TouchEvent): void };
let onTouchEnd: { (event: TouchEvent): void };
let onMouseMove: { (event: MouseEvent): void };
let onMouseUp: { (event: MouseEvent): void };

onMounted(() => {
    if (env.touch) {
        onTouchMove = (e: TouchEvent) => {
            mouseX.value = e.touches[0].pageX;
            mouseY.value = e.touches[0].pageY;
            if (drag.value >= 0) dragHandler();
        };
        onTouchEnd = (e: TouchEvent) => {
            dragStop();
        };
        document.addEventListener("touchmove", onTouchMove);
        document.addEventListener("touchend", onTouchEnd);
    } else {
        onMouseMove = (e: MouseEvent) => {
            mouseX.value = e.pageX;
            mouseY.value = e.pageY;
            if (drag.value >= 0) dragHandler();
        };
        onMouseUp = (e: MouseEvent) => {
            dragStop();
        };
        document.addEventListener("mousemove", onMouseMove);
        document.addEventListener("mouseup", onMouseUp);
    }
});

/** This code is here because array can be changed from outside and partsArray must be synchronized to these changes */
watch(value, (newVal) => {
	const newIsArray = Array.isArray(newVal);
	const len = newIsArray ? newVal.length : 0;
	const palen = partsArray.value.length;
	if (len > palen) {
		while(partsArray.value.length < len) {
			partsArray.value.push(generatePartForIndex(partsArray.value.length));
		}
	} else if (len < palen) {
		while (partsArray.value.length > len) {
			partsArray.value.splice(partsArray.value.length - 1, 1);	// delete last item
		}
	}
}, { deep: true });


onUnmounted(() => {
    if (env.touch) {
        document.removeEventListener("touchmove", onTouchMove);
        document.removeEventListener("touchend", onTouchEnd);
    } else {
        document.removeEventListener("mousemove", onMouseMove);
        document.removeEventListener("mouseup", onMouseUp);
    }
});

defineExpose({
	discardItems,
	focusItem,
	insertItem,
	deleteItem
});
</script>

<style>
.fd-array-focus {
}

.fd-array {
	position: relative;
	display: flex;
	flex-direction: column;
	row-gap: calc(2 * var(--form-v-padding));
	width: 100%;	/* TB: added 17.4.25 to make array items grow (hope doesn't cause other problems) */
}

.fd-array-item {
	display: flex;
	flex-direction: row;
	align-items: center;
	column-gap: var(--input-spacing);
	/* margin: var(--form-v-padding) 0; */ /* was problematic for arrays inside arrays */
}

.fd-array-item-content {
	flex: 1;
}

.fd-array-handle {
	width: 22px;
	color: var(--fg);
	opacity: 0.2;
	cursor: grab;
	user-select: none;
	-webkit-user-select: none;
	transition: opacity 400ms;
	flex: 0 0;
}

.fd-array-handle:hover {
	opacity: 1;
}

.fd-array-remove-item {
	color: var(--fg);
	opacity: 0.2;
	cursor: pointer;
	user-select: none;
	-webkit-user-select: none;
	transition: opacity 400ms;
}

.fd-array-remove-item:hover {
	color: var(--red);
	opacity: 1;
}

.fd-array-placeholder-item {
	box-shadow: var(--drawer-shadow);
}
.fd-array-placeholder-item:before {
	content: 'x';
	color: #00000000;	/* x won't be visible ... it's there to prevent jumpy baseline alignment */
}

.fd-array-add-item {
	display: flex;
	flex-direction: row;
	justify-content: flex-start;
	align-items: baseline;
	flex-wrap: wrap;
	column-gap: var(--input-spacing);
	row-gap: 4px;
}

.fd-array-add-button {
	cursor: pointer;
}

.fd-array-add-button > .fi {
	margin-right: 4px;
}
</style>
